
import { makeStyles, useTheme } from "@material-ui/core/styles";
import React from "react";

import Box from "@material-ui/core/Box";
import CardActions from "@material-ui/core/CardActions";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import Pagination from "@material-ui/lab/Pagination";
// core componentssrc
import { Error, Warning } from "@material-ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic";
import TableBackdrop from "components/Backdrops/TableBackdrop/TableBackdrop";
import GenericButton from "components/Buttons/GenericButton";
import CardTableContainer from "components/Cards/Tables/CardTableContainer";
import { bulkUpdateWorkCenterEmployeesStatus, listWorkCenterEmployers, listWorkCenterEmployersRENT } from "crud/workCentersEmployer.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import useMessage from "hooks/useMessage";
import moment from "moment";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { hasContractSalaryAlert, SALARY_ALERT_TOOLTIP } from "utils/salaryAlert";
import { formatMonetaryValue } from "utils/string_utils";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const ResultSection = ({ 
  handleOpenDialog, 
  detailsEnabled = true, 
  FromRENT = false, 
  setTotalWorkerCount, 
  sortColumn, 
  setSortColumn, 
  sortDirection, 
  setSortDirection,
  bulkSelectionMode = false,
  selectedEmployees = [],
  setSelectedEmployees = () => {},
  onBulkAction = () => {},
  getBulkActionCallbacks = () => {},
  onPaginationChange,
  onResetToFirstPage,
}) => {
  const theme = useTheme();
  const classes = useStylesGeneric();
  const { showSuccessMessage, showErrorMessage } = useMessage();
  const queryClient = useQueryClient();

  const { watch, setValue, getValues } = useFormContext();
  
  const fetchWorkCenterEmployees = async () => {
    const values = getValues();
    
    const queryParams = {
      ...values,
      startDate: values?.startDate ? moment(values.startDate).format() : null,
      endDate: values?.endDate ? moment(values.endDate).format() : null,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    };

    if (FromRENT) {
      return listWorkCenterEmployersRENT(queryParams);
    }
    return listWorkCenterEmployers(queryParams);
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["listWorkCenterEmployers"],
    queryFn: fetchWorkCenterEmployees,
    staleTime: 0,
    refetchOnMount: true,
    enabled: FromRENT,
  });

  const page = watch("pageIndex");
  const salaryAlertFilterValue = watch("SalaryAlertOn");
  const salaryAlertFilterIsOn = hasContractSalaryAlert({ SalaryAlertOn: salaryAlertFilterValue });

  const { mutateAsync: bulkUpdateStatusMutation, isLoading: bulkUpdateLoading } = useMutation(
    ({ ids, active, motiveId, otherMotiveDescription }) =>
      bulkUpdateWorkCenterEmployeesStatus(ids, active, { motiveId, otherMotiveDescription }),
    {
      onSuccess: (_, variables) => {
        const action = variables.active ? "activados" : "inactivados";
        showSuccessMessage(`Trabalhadores ${action} com sucesso.`);
        queryClient.invalidateQueries(["listWorkCenterEmployers"]);
      },
      onError: (_, variables) => {
        const action = variables.active ? "activar" : "inactivar";
        showErrorMessage(`Ocorreu um erro ao ${action} os trabalhadores!`);
      },
    }
  );

  const tableHead = [
    { label: " ", sortKey: null },
    { label: "Data de Registo", sortKey: "created" },
    { label: "Nome do Trabalhador", sortKey: "name" },
    { label: "NISS", sortKey: "ssn" },
    { label: "NIF", sortKey: "nif" },
    { label: "N.º Documento de Identificação", sortKey: "identityDocumentNumber" },
    { label: "Centro de Trabalho", sortKey: "workCenterDescription" },
    { label: "Situação Profissional", sortKey: "employeeStatusDescription" },
    { label: "Categoria Profissional", sortKey: "professionalCategoryDescription" },
    { label: "Remuneração Total", sortKey: "totalSalary" },
    { label: "Data de actualização", sortKey: "lastModified" },
  ];

  const handleSort = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    
    if (!column) return;
    
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setValue("pageIndex", 1);
    onResetToFirstPage?.();
    
    setTimeout(() => {
      refetch();
    }, 0);
  };

  const renderSortIcon = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    if (!column) return null;
    
    if (sortColumn === column) {
      return sortDirection === "desc" ? (
        <ArrowUpwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      ) : (
        <ArrowDownwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      );
    }
    return <UnfoldMoreIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle", opacity: 0.3 }} />;
  };

  const handleOpenDetails = (id, isCreating) => {
    handleOpenDialog(id, isCreating);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = data?.data?.map((employee) => employee.id) || [];
      setSelectedEmployees(allIds);
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const isAllSelected = data?.data?.length > 0 && selectedEmployees.length === data?.data?.length;
  const isSomeSelected = selectedEmployees.length > 0 && selectedEmployees.length < (data?.data?.length || 0);

  const handleBulkInactivateWithMotive = useCallback(async ({ motiveId, otherMotiveDescription } = {}) => {
    return bulkUpdateStatusMutation({
      ids: selectedEmployees,
      active: false,
      motiveId,
      otherMotiveDescription,
    });
  }, [bulkUpdateStatusMutation, selectedEmployees]);

  const handleBulkActivate = useCallback(async () => {
    return bulkUpdateStatusMutation({ ids: selectedEmployees, active: true });
  }, [bulkUpdateStatusMutation, selectedEmployees]);

  // Fornecer callbacks para o componente pai
  React.useEffect(() => {
    getBulkActionCallbacks({
      activate: handleBulkActivate,
      inactivate: handleBulkInactivateWithMotive,
    });
  }, [getBulkActionCallbacks, handleBulkActivate, handleBulkInactivateWithMotive]);

  if(FromRENT) {
    setTotalWorkerCount(data?.totalCount)
  }

  const tableHeadWithCheckbox = bulkSelectionMode 
    ? [{ label: "", sortKey: null, isCheckbox: true }, ...tableHead]
    : tableHead;

  return (
    <>
      <ConditionGuard condition={bulkSelectionMode && selectedEmployees.length > 0}>
        <Box display="flex" gridGap={12} marginBottom="1rem">
          <GenericButton 
            color="primary" 
            onClick={() => onBulkAction("activate")}
            disabled={bulkUpdateLoading}
          >
            Activar
          </GenericButton>
          <GenericButton 
            color="secondary" 
            onClick={() => onBulkAction("inactivate")}
            disabled={bulkUpdateLoading}
          >
            Inactivar
          </GenericButton>
        </Box>
      </ConditionGuard>
      <CardTableContainer title={"Lista de Trabalhadores Registados Na Sua Empresa"} count={data?.totalCount}>
        <TableContainer>
          <ConditionGuard condition={!isLoading} fallback={<TableBackdrop open />}>
            <Box component={Table} alignItems="center" marginBottom="0!important">
              <TableHead>
                <TableRow>
                  {tableHeadWithCheckbox.map((prop, key) => (
                    <TableCell
                      key={key}
                      classes={{
                        root: classes.tableCellRoot + " " + classes.tableCellRootHead,
                      }}
                      onClick={() => !prop.isCheckbox && prop.sortKey && handleSort(key - (bulkSelectionMode ? 1 : 0))}
                      style={prop.sortKey ? { cursor: "pointer", userSelect: "none" } : {}}
                    >
                      {prop.isCheckbox ? (
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isSomeSelected}
                          onChange={handleSelectAll}
                          color="primary"
                        />
                      ) : (
                        <Box display="flex" alignItems="center">
                          {prop.label}
                          {prop.sortKey && renderSortIcon(key - (bulkSelectionMode ? 1 : 0))}
                        </Box>
                      )}
                    </TableCell>
                  ))}
                  <ConditionGuard condition={detailsEnabled}>
                    <TableCell
                      key="detailsCol"
                      classes={{
                        root:
                          classes.tableCellRoot +
                          " " +
                          classes.tableCellRootHead +
                          " " +
                          classes.tableCellHeadStickyRight,
                      }}
                    >
                      Detalhes
                    </TableCell>
                  </ConditionGuard>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.data?.map((prop, key) => (
                  <TableRow key={key}>
                    <ConditionGuard condition={bulkSelectionMode}>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        <Checkbox
                          checked={selectedEmployees.includes(prop.id)}
                          onChange={() => handleSelectEmployee(prop.id)}
                          color="primary"
                        />
                      </TableCell>
                    </ConditionGuard>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      <Box display="flex" alignItems="center" gridGap={4}>
                        {!FromRENT && !prop.isActive && (
                          <Tooltip title={"Este trabalhador está inativo."} placement="top">
                            <Error color="error" />
                          </Tooltip>
                        )}
                        {!FromRENT && prop.isExpiryWithin30Days && (
                          <Tooltip title={"Documentos a expirar em breve ou expirados."} placement="top">
                            <Warning color="error" />
                          </Tooltip>
                        )}
                        {!FromRENT && (hasContractSalaryAlert(prop) || salaryAlertFilterIsOn) && (
                          <Tooltip title={SALARY_ALERT_TOOLTIP} placement="top">
                            <Warning color="error" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {moment(prop.created).format("DD-MM-YYYY")}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{prop.name}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{prop.ssn}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{prop.nif}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{prop.identityDocumentNumber}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{prop.workCenterDescription}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{prop.employeeStatusDescription}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {prop.professionalCategoryDescription}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {formatMonetaryValue(prop.totalSalary)}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {prop.lastModified ? moment(prop.lastModified).format("DD-MM-YYYY") : '-'}
                    </TableCell>
                    <ConditionGuard condition={detailsEnabled}>
                      <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellBodyStickyRight }}>
                        <Tooltip title={"Detalhes"}>
                          <IconButton onClick={() => handleOpenDetails(prop.id)}>
                            <Box
                              component={ArrowForwardIosIcon}
                              width="12px!important"
                              height="12px!important"
                              color={theme.palette.primary.main}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </ConditionGuard>
                  </TableRow>
                ))}
              </TableBody>
            </Box>
          </ConditionGuard>
        </TableContainer>

        <Box classes={{ root: classes.cardActionsRoot }} component={CardActions} justifyContent="flex-end"></Box>

        <Pagination
          className={classes.alignPagination}
          count={data?.totalPages}
          page={page}
          onChange={(_, value) => {
            onPaginationChange?.(value);
          }}
          color="primary"
        />
      </CardTableContainer>

      {!isLoading && data?.data?.length === 0 && (
        <Box classes={{ root: classes.noResultsInfo }}>Não foram encontrados resultados para esta pesquisa.</Box>
      )}
    </>
  );
};

export default ResultSection;
